import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../../../../components/Navbar';
import Footer from '../../../../../home/components/Footer';

const AREA = 'Carpenders Park';
const AREA_SLUG = 'carpenders-park';
const POSTCODE = 'HA5';
const PHONE = '020 3198 1818';
const PHONE_RAW = '02031981818';

const whatsIncluded = [
  { icon: 'ri-file-list-3-line', title: 'Full Circuit Inspection', desc: `Comprehensive testing of all electrical circuits and wiring throughout your ${AREA} property to BS 7671 standards.` },
  { icon: 'ri-search-eye-line', title: 'Detailed Testing', desc: 'Earth continuity, insulation resistance, polarity, and RCD testing with calibrated professional equipment.' },
  { icon: 'ri-bar-chart-line', title: 'Condition Report', desc: 'Professional EICR certificate with coded observations (C1, C2, C3) and clear remedial recommendations.' },
  { icon: 'ri-shield-check-line', title: 'Compliance Certificate', desc: `Official NICEIC certification for landlords, homeowners, and insurance requirements in ${AREA}.` },
  { icon: 'ri-tools-line', title: 'Remedial Guidance', desc: 'Clear guidance on any issues found with priority recommendations for repairs and next steps.' },
  { icon: 'ri-smartphone-line', title: 'Digital Records', desc: 'Electronic copy of your EICR certificate emailed same day for easy storage and sharing.' },
];

const benefits = [
  { icon: 'ri-home-4-line', title: 'Legal Compliance', desc: `Meet landlord obligations and insurance requirements with official EICR certification in ${AREA}.` },
  { icon: 'ri-flashlight-line', title: 'Safety Assurance', desc: 'Identify potential electrical hazards before they become dangerous or costly to fix.' },
  { icon: 'ri-money-pound-circle-line', title: 'Property Value', desc: 'Maintain property value and provide peace of mind for buyers and tenants.' },
  { icon: 'ri-user-star-line', title: 'Expert Testing', desc: 'NICEIC approved electricians with specialist testing equipment and years of local expertise.' },
];

const areas = [
  { name: 'Watford Town Centre', slug: 'watford-town-centre' },
  { name: 'Oxhey', slug: 'oxhey' },
  { name: 'Bushey', slug: 'bushey' },
  { name: 'Croxley Green', slug: 'croxley-green' },
  { name: 'Abbots Langley', slug: 'abbots-langley' },
  { name: 'Rickmansworth', slug: 'rickmansworth' },
  { name: 'Garston', slug: 'garston' },
  { name: 'North Watford', slug: 'north-watford' },
  { name: 'West Watford', slug: 'west-watford' },
  { name: 'Leavesden', slug: 'leavesden' },
  { name: 'Nascot Wood', slug: 'nascot-wood' },
  { name: 'Holywell', slug: 'holywell' },
  { name: 'Meriden', slug: 'meriden' },
  { name: 'Cassiobury', slug: 'cassiobury' },
];

export default function CarpendersParkEICRPage() {
  useEffect(() => {
    document.title = `EICR Testing ${AREA} ${POSTCODE} | Electrical Condition Reports | Beyond Electrical`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', `Professional EICR testing in ${AREA}, ${POSTCODE}. Electrical Installation Condition Reports for landlords, homeowners & businesses. NICEIC approved. Same-day certificates. Call ${PHONE}.`);
    const metaKw = document.querySelector('meta[name="keywords"]');
    if (metaKw) metaKw.setAttribute('content', `EICR testing ${AREA}, electrical condition report ${AREA}, landlord certificate ${POSTCODE}, NICEIC electrician ${AREA}, EICR ${POSTCODE}`);
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); (canonical as HTMLLinkElement).rel = 'canonical'; document.head.appendChild(canonical); }
    (canonical as HTMLLinkElement).href = `https://beyondelectrical.co.uk/services/safety-compliance/eicr-testing/${AREA_SLUG}`;
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = `eicr-${AREA_SLUG}-schema`;
    script.text = JSON.stringify({ '@context': 'https://schema.org', '@type': 'Service', 'serviceType': 'EICR Testing', 'provider': { '@type': 'LocalBusiness', 'name': 'Beyond Electrical', 'telephone': PHONE_RAW }, 'areaServed': { '@type': 'City', 'name': AREA } });
    document.head.appendChild(script);
    return () => { const s = document.getElementById(`eicr-${AREA_SLUG}-schema`); if (s) document.head.removeChild(s); };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628] via-[#1a2942] to-[#0A1628]"></div>
        <div className="absolute inset-0 opacity-10">
          <img src="https://public.readdy.ai/ai/img_res/e50252870c3a55d85d29fbe9f777bd20.jpg" alt={`EICR Testing ${AREA}`} className="w-full h-full object-cover object-top" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        <div className="relative max-w-7xl mx-auto px-6 w-full">
          <div className="flex items-center gap-4 mb-6">
            <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20"><span className="text-white text-sm font-medium">🏆 NICEIC Approved</span></div>
            <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20"><span className="text-white text-sm font-medium">📍 {AREA}</span></div>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">EICR Testing<br /><span style={{color:'#D4AF37'}}>in {AREA}</span></h1>
          <p className="text-xl text-gray-200 mb-8 max-w-2xl leading-relaxed">Professional Electrical Installation Condition Reports for landlords, homeowners, and businesses in {AREA}. NICEIC certified testing to BS 7671 standards with same-day certificates available.</p>
          <div className="flex flex-wrap gap-4">
            <a href={`tel:${PHONE_RAW}`} className="px-8 py-4 rounded-lg font-semibold text-lg hover:opacity-90 transition-all shadow-lg whitespace-nowrap flex items-center gap-2" style={{backgroundColor:'#D4AF37',color:'#0A1628'}}><i className="ri-phone-line"></i> Call {PHONE}</a>
            <Link to="/contact" className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white/20 transition-all border border-white/30 whitespace-nowrap">Get Free Quote</Link>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#0A1628] mb-4">What&apos;s Included in Our EICR Testing</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">Comprehensive electrical inspection and testing for {AREA} properties with full certification</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whatsIncluded.map((item, i) => (
              <div key={i} className="bg-white p-8 rounded-xl border border-gray-100 hover:border-[#D4AF37] transition-all duration-300">
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-[#0A1628] to-[#1a2942] rounded-lg mb-6"><i className={`${item.icon} text-3xl text-[#D4AF37]`}></i></div>
                <h3 className="text-xl font-bold text-[#0A1628] mb-3">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#0A1628] mb-4">Why Get an EICR in {AREA}?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">Essential electrical safety certification for {POSTCODE} property owners and landlords</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((b, i) => (
              <div key={i} className="text-center p-6 rounded-xl hover:bg-gray-50 transition-all duration-300">
                <div className="w-20 h-20 flex items-center justify-center rounded-full mb-6 mx-auto" style={{background:'linear-gradient(135deg,#D4AF37,#C5A028)'}}><i className={`${b.icon} text-4xl text-white`}></i></div>
                <h3 className="text-xl font-bold text-[#0A1628] mb-3">{b.title}</h3>
                <p className="text-gray-600 leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-[#0A1628] via-[#1a2942] to-[#0A1628] text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Why Choose Beyond Electrical?</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">{AREA}&apos;s trusted NICEIC approved electricians for professional EICR testing</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {[{ icon: 'ri-shield-check-line', title: 'NICEIC Approved', desc: 'Official certification you can trust' }, { icon: 'ri-time-line', title: 'Fast Turnaround', desc: 'Same-day certificates available' }, { icon: 'ri-file-list-3-line', title: 'Detailed Reports', desc: 'Clear, comprehensive documentation' }, { icon: 'ri-customer-service-2-line', title: 'Expert Advice', desc: 'Professional guidance included' }].map((item, i) => (
              <div key={i} className="text-center p-6">
                <div className="w-16 h-16 flex items-center justify-center rounded-full mb-4 mx-auto" style={{backgroundColor:'#D4AF37'}}><i className={`${item.icon} text-3xl text-[#0A1628]`}></i></div>
                <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                <p className="text-gray-300 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[['500+', 'EICRs Completed'], ['100%', 'Satisfaction Rate'], ['Same Day', 'Certificates'], ['15+', 'Years Experience']].map(([num, label], i) => (
              <div key={i}><div className="text-4xl font-bold mb-1" style={{color:'#D4AF37'}}>{num}</div><div className="text-gray-300 text-sm">{label}</div></div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#0A1628] mb-4">Also Covering Nearby Areas</h2>
            <p className="text-xl text-gray-600">EICR testing available across Watford and surrounding areas</p>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {areas.map((area, i) => (
              <Link key={i} to={`/services/safety-compliance/eicr-testing/${area.slug}`} className="px-6 py-3 bg-white rounded-full text-[#0A1628] font-medium border border-gray-200 hover:border-[#D4AF37] hover:bg-[#D4AF37] hover:text-white transition-all duration-300 cursor-pointer shadow-sm">{area.name}</Link>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/services/safety-compliance/eicr-testing" className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#B8941F] font-medium transition-colors"><i className="ri-arrow-left-line"></i> View All EICR Testing Services</Link>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-[#0A1628] via-[#1a2942] to-[#0A1628] text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Need an EICR in {AREA}?</h2>
          <p className="text-xl text-gray-300 mb-8 leading-relaxed">Get your professional Electrical Installation Condition Report from NICEIC approved electricians. Same-day service available in {AREA}, {POSTCODE}.</p>
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <a href={`tel:${PHONE_RAW}`} className="px-8 py-4 rounded-lg font-semibold text-lg hover:opacity-90 transition-all shadow-lg whitespace-nowrap flex items-center gap-2" style={{backgroundColor:'#D4AF37',color:'#0A1628'}}><i className="ri-phone-line"></i> Call {PHONE}</a>
            <Link to="/contact" className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white/20 transition-all border border-white/30 whitespace-nowrap">Request Callback</Link>
          </div>
          <div className="flex flex-wrap justify-center gap-8 text-sm text-gray-300">
            <div className="flex items-center gap-2"><i className="ri-check-line text-xl" style={{color:'#D4AF37'}}></i><span>Same-Day Certificates</span></div>
            <div className="flex items-center gap-2"><i className="ri-check-line text-xl" style={{color:'#D4AF37'}}></i><span>NICEIC Approved</span></div>
            <div className="flex items-center gap-2"><i className="ri-check-line text-xl" style={{color:'#D4AF37'}}></i><span>Competitive Pricing</span></div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
